<template>
  <div
    v-if="orderedMeetingIds.length > 0"
    class="tw-max-w-condensed-content tw-flex tw-flex-col tw-gap-4 tw-mx-auto"
    data-role="past-meetings-list"
  >
    <div
      v-for="(meetingId, meetingIndex) in orderedMeetingIds"
      :key="meetingId"
    >
      <MeetingCard
        :latest-meeting="meetingIndex === 0"
        :meeting="meetingsById[meetingId]"
      />
    </div>

    <DeleteMeetingModal />
    <MoveBackToOpenConfirmationModal />
  </div>
  <PastMeetingsEmpty v-else :talking-point-tag="talkingPointTag" />
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount, inject } from 'vue'
import { useStore } from 'vuex'
import { storeToRefs } from 'pinia'

import type { PastPageData } from '@app/application/client_meetings/types'
import { isUsingNewLayout } from '@app/adapters/silverfin'
import scrollHelper from '@app/application/communication/sections/communication/components/mixins/scroll-helper.js'
import { usePastMeetingsStore } from '@app/application/client_meetings/screens/past/stores/past-meetings'
import { useMeetingProposalUpdates } from '@app/application/client_meetings/screens/past/composables/use-meeting-proposal-updates'
import { useTalkingPointTag } from '@app/application/client_meetings/composables/use-talking-point-tag'

import MeetingCard from './meeting-card.vue'
import PastMeetingsEmpty from './past-meetings-empty.vue'
import DeleteMeetingModal from '@app/application/client_meetings/screens/past/components/delete-meeting-modal.vue'
import MoveBackToOpenConfirmationModal from '@app/application/client_meetings/screens/past/components/move-back-to-open-confirmation-modal.vue'

const pageData = inject('pageData') as PastPageData

interface ScrollMixinType {
  elementAnchorLink: () => string | null
  anchorElementScrolled: boolean
}

defineOptions({
  mixins: [scrollHelper],
  methods: {
    async handleAnchorScroll(callback: (() => void) | null = null) {
      const anchorSelector = (
        this as unknown as ScrollMixinType
      ).elementAnchorLink?.()

      if (!anchorSelector) return

      const target = document
        .querySelector('[data-role="past-meetings-list"]')
        ?.querySelector(anchorSelector) as HTMLElement

      // Find closest scrollable parent container
      const container = target?.closest('.tw-overflow-y-auto') as HTMLElement

      if (!container || !target) return

      const scrollOffset = target.offsetTop - container.offsetTop

      const usingNewLayout = isUsingNewLayout()

      if (usingNewLayout) {
        target?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      } else {
        container.scrollTo({
          top: scrollOffset,
          behavior: 'smooth',
        })
      }
      ;(this as unknown as ScrollMixinType).anchorElementScrolled = true

      if (callback) {
        callback()
      }
    },
  },
})

const pastMeetingsStore = usePastMeetingsStore()
useMeetingProposalUpdates({
  channelName: pageData.messageBus.channelName,
  lastId: pageData.messageBus.lastId,
})

const store = useStore()

const { orderedMeetingIds, meetingsById } = storeToRefs(pastMeetingsStore)

const { talkingPointTag } = useTalkingPointTag()

const { setPastMeetings, fetchPastMeetings } = pastMeetingsStore

async function refreshCommunicationData(
  data: { action?: string; remark?: { id: string } } = {}
) {
  await store.dispatch('fetchCommunication')

  if (data.action === 'createRemark') {
    window.eventHub.$emit('highlightRemark', {
      elementId: `remark-${data.remark?.id}`,
      force: true,
    })
  }
}

setPastMeetings(pageData.meetings)
refreshCommunicationData()

store.dispatch('fetchMentionUsernames')

onMounted(() => {
  window.eventHub.$on('refreshCommunicationData', refreshCommunicationData)
  window.eventHub.$on('refreshClientMeetings', fetchPastMeetings)
})

onBeforeUnmount(() => {
  window.eventHub.$off('refreshClientMeetings', fetchPastMeetings)
  window.eventHub.$off('refreshCommunicationData', refreshCommunicationData)
})
</script>
